import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import {
  IBillingAccount,
  IBillingDocument,
  transactionService,
} from '@dch/billing-business-logic';
import { Store } from '@ngrx/store';
import { ApplicationState } from 'src/app/state/reducers';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';
import { filter, tap } from 'rxjs/operators';
import { OnDestroyHookDirective } from '../../common/hooks';
import { TranslationService, EnvironmentService } from '@dch/ng-common';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';
import {
  getThirdPartyTransactionReferenceForOrderId,
  isDocumentLesserThan12MonthsOld,
} from '../../services/utils.service';
import { TransactionReferenceService } from '../../services/transaction-reference.service';

@Component({
  selector: 'my-bill-download-card',
  templateUrl: './my-bill-download-card.component.html',
  standalone: false,
})
export class MyBillDownloadCardComponent
  extends OnDestroyHookDirective
  implements OnInit, OnDestroy
{
  @Input() billDocument: IBillingDocument;
  @Input() billingAccount: IBillingAccount;
  @Input() buttonZone = 'mybill';
  thirdPartyReference?: string;
  messageBoxTitle?: string;
  messageBoxContent?: string;
  messageBoxTypeError = MessageBoxTypesEnum.Error;
  isResUser: boolean;
  showCsvDownload = false;
  showCsvMessageInvoiceOlderThan12Months = false;
  hasCsvError = false;
  hasUBLXmlApiActive = false;
  showXmlDownload = false;
  customerId: number;

  readonly selectedCustomerAccount$ = this.store.select(selectCustomerAccount);

  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private environmentService: EnvironmentService,
    private transactionReferenceService: TransactionReferenceService
  ) {
    super();
  }

  ngOnInit() {
    this.addSubscription(
      this.selectedCustomerAccount$
        .pipe(
          filter(
            (selectedCustomerAccount) => selectedCustomerAccount !== undefined
          ),
          tap((selectedCustomerAccount) => {
            this.isResUser =
              selectedCustomerAccount.getSelectedCustomer().subsegment ===
              'RES';
            this.customerId =
              selectedCustomerAccount.getSelectedCustomer().customerId;

            this.showCsvDownload =
              !this.isResUser &&
              !this.environmentService.isProximusPlusMobileApp &&
              !this.environmentService.isMobile &&
              transactionService.getPDFLinksAndDetails(this.billDocument)
                .showDetails;

            this.showCsvMessageInvoiceOlderThan12Months =
              this.showCsvDownload &&
              !isDocumentLesserThan12MonthsOld(this.billDocument.documentDate);
          })
        )
        .subscribe()
    );
    this.handleUBLXmlDownloadLinksVisibility();
  }

  handleUBLXmlDownloadLinksVisibility() {
    this.transactionReferenceService
      .getDocumentReferencesForOrderId(
        this.billingAccount.accountNumber,
        this.billDocument.orderId
      )
      .pipe(
        tap((documentReferences) => {
          if (documentReferences) {
            this.showXmlDownload = documentReferences.showUblDownload;
            if (this.showXmlDownload) {
              this.thirdPartyReference =
                getThirdPartyTransactionReferenceForOrderId(
                  documentReferences.documents
                );
            }
          }
        })
      )
      .subscribe();
  }

  handleDownloadError({
    hasCsvError,
    invoiceError,
  }: {
    hasCsvError: boolean;
    invoiceError?: boolean;
  }) {
    if (!hasCsvError && !invoiceError) {
      this.hasCsvError = false;
    }
    if (hasCsvError) {
      this.hasCsvError = true;
      this.messageBoxTitle = this.translationService.getTranslatedValueFor(
        'bills.cover.usageLevel4.errorAlert.downloadCSV.title'
      );
      this.messageBoxContent = this.translationService.getTranslatedValueFor(
        'bills.cover.usageLevel4.errorAlert.downloadCSV.message'
      );
    } else if (invoiceError) {
      this.hasCsvError = true;
      this.messageBoxTitle = this.translationService.getTranslatedValueFor(
        'bills.pdfScreen.error.downloadTitle'
      );
      this.messageBoxContent = this.translationService.getTranslatedValueFor(
        'bills.pdfScreen.error.download'
      );
    }
  }
}
